import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, FileSpreadsheet, Loader2, UploadCloud, X, AlertTriangle } from "lucide-react";
import { createAudit } from "@/lib/api";
import { Eyebrow, TopBar, Footer, SSMark } from "@/components/SiteChrome";

const MAX_SIZE = 30 * 1024 * 1024;
const VALID_EXT = /\.xlsx$/i;

const CURRENCIES = [
  { value: "", label: "Auto-detect from report" },
  { value: "£", label: "£ GBP" },
  { value: "€", label: "€ EUR" },
  { value: "$", label: "$ USD" },
];

const Analysing = ({ step }) => (
  <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="analysing-state">
    <div className="text-center max-w-md w-full">
      <SSMark />
      <Eyebrow className="mt-6 mb-5 justify-center">Running the 77-point check</Eyebrow>
      <h1 className="font-display text-4xl sm:text-5xl text-[color:var(--ink)]" data-testid="analysing-headline">
        {step === 0 ? "Reading your campaigns…" : "Building your scorecard…"}
      </h1>
      <div className="mt-10 flex justify-center">
        <Loader2 size={28} strokeWidth={2} className="animate-spin text-[color:var(--green)]" />
      </div>
    </div>
  </div>
);

const Instructions = () => (
  <div className="ss-panel border-l-4 border-l-[color:var(--green)] px-6 sm:px-8 py-6" data-testid="bulk-instructions">
    <p className="ss-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--green)]">
      How to get your bulk file (2 minutes)
    </p>
    <ol className="mt-4 space-y-3 text-sm text-[color:var(--ink)] leading-relaxed">
      <li className="flex gap-3">
        <span className="ss-mono text-xs font-semibold text-[color:var(--green)] w-5 shrink-0">01</span>
        <span>In Seller Central go to <span className="font-semibold">Sponsored ads → Sponsored ads campaigns</span>.</span>
      </li>
      <li className="flex gap-3">
        <span className="ss-mono text-xs font-semibold text-[color:var(--green)] w-5 shrink-0">02</span>
        <span>Open <span className="font-semibold">Bulk operations</span>.</span>
      </li>
      <li className="flex gap-3">
        <span className="ss-mono text-xs font-semibold text-[color:var(--green)] w-5 shrink-0">03</span>
        <span>Set the date range to the <span className="font-semibold">last 30 or 60 days</span>, and tick <span className="font-semibold">include the search term report</span>.</span>
      </li>
      <li className="flex gap-3">
        <span className="ss-mono text-xs font-semibold text-[color:var(--green)] w-5 shrink-0">04</span>
        <span>Click <span className="font-semibold">Create spreadsheet for download</span>, wait for it to finish, then download the .xlsx.</span>
      </li>
      <li className="flex gap-3">
        <span className="ss-mono text-xs font-semibold text-[color:var(--green)] w-5 shrink-0">05</span>
        <span>Upload it here.</span>
      </li>
    </ol>
  </div>
);

export default function UploadPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [brand, setBrand] = useState("");
  const [currency, setCurrency] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState("form");
  const [step, setStep] = useState(0);
  const [error, setError] = useState(location.state?.notice || null);
  const [fileError, setFileError] = useState(null);
  const [brandError, setBrandError] = useState(null);

  const canSubmit = !!file && brand.trim().length > 0 && phase === "form";

  useEffect(() => {
    if (phase !== "analysing") return;
    setStep(0);
    const id = setTimeout(() => setStep(1), 3500);
    return () => clearTimeout(id);
  }, [phase]);

  const acceptFile = (f) => {
    setFileError(null);
    if (!VALID_EXT.test(f.name)) {
      setFileError("Please upload the .xlsx bulk file you downloaded from Seller Central.");
      return;
    }
    if (f.size > MAX_SIZE) {
      setFileError(`${f.name}: over the 30MB limit.`);
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    setError(null);
    setBrandError(null);
    if (!brand.trim()) {
      setBrandError("Add your brand name so we can separate your branded searches from everything else.");
      return;
    }
    if (!file) return;

    setPhase("analysing");
    try {
      const res = await createAudit(file, {
        brand: brand.trim(),
        currency: currency || undefined,
      });
      sessionStorage.setItem(`summary_${res.auditId}`, JSON.stringify(res.summary));
      if (res.fileResult) {
        sessionStorage.setItem(`fileResult_${res.auditId}`, JSON.stringify(res.fileResult));
      }
      navigate(`/audit/${res.auditId}`, {
        state: { summary: res.summary, fileResult: res.fileResult, brandName: brand.trim() },
      });
    } catch (e) {
      const data = e.response?.data;
      // Backend error codes come with prospect-facing messages — display verbatim.
      if (data?.message) {
        setError(data.message);
      } else if (data?.error) {
        setError(data.error);
      } else {
        setError("Something went wrong building your scorecard. Try again — or email us your report at hello@sellersside.com and we'll run it manually.");
      }
      setPhase("form");
    }
  };

  if (phase === "analysing") return <Analysing step={step} />;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <TopBar />
      <main className="flex-1 mx-auto max-w-3xl w-full px-6 pb-24">
        {/* Hero */}
        <section className="pt-16 sm:pt-24 pb-12">
          <Eyebrow className="mb-6 anim-fade-up anim-d1">Free scorecard · No obligation</Eyebrow>
          <h1
            className="font-display text-[44px] sm:text-6xl lg:text-7xl leading-[0.98] text-[color:var(--ink)] anim-fade-up anim-d2 max-w-2xl"
            data-testid="hero-headline"
          >
            the 77-point<br />
            profit check.<br />
            <span className="block mt-4 sm:mt-6 text-[36px] sm:text-5xl lg:text-6xl">
              60 seconds to see<br />
              what your amazon account<br />
              is <span className="text-[color:var(--green)]">leaving behind</span>
            </span>
          </h1>
          <p className="mt-8 text-base sm:text-lg text-[color:var(--grey)] max-w-xl anim-fade-up anim-d3">
            Upload one Amazon bulk file. Get a quantified profit scorecard — savings and sales growth, line by line — in under 60 seconds.
          </p>
        </section>

        {/* Instructions block */}
        <div className="anim-fade-up anim-d3">
          <Instructions />
          <div className="mt-2 flex items-center justify-center text-[color:var(--grey)]">
            <ChevronDown size={20} strokeWidth={2} />
          </div>
        </div>

        {error && (
          <div className="mt-8 ss-panel border-l-4 border-l-[color:var(--bad)] px-5 py-4 text-sm text-[color:var(--ink)]" data-testid="error-banner">
            <p className="ss-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--bad)] mb-1">
              We couldn't build your scorecard
            </p>
            {error}
          </div>
        )}
        {fileError && (
          <div className="mt-6 ss-panel border-l-4 border-l-[color:var(--warn)] px-5 py-4 text-sm text-[color:var(--ink)]" data-testid="file-error">
            <p className="ss-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--warn)] mb-1">
              File
            </p>
            {fileError}
          </div>
        )}

        {/* Dropzone */}
        <div className="mt-8 anim-fade-up anim-d4">
          {!file ? (
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) acceptFile(e.dataTransfer.files[0]); }}
              className={`cursor-pointer border-2 border-dashed p-10 sm:p-14 text-center transition-colors ${
                dragOver
                  ? "border-[color:var(--green)] bg-[color:var(--green-soft)]"
                  : "border-[color:var(--ink)] bg-[color:var(--panel)] hover:bg-[color:var(--green-soft)]"
              }`}
              data-testid="upload-dropzone"
            >
              <UploadCloud size={36} strokeWidth={1.5} className="mx-auto text-[color:var(--ink)]" />
              <p className="mt-4 font-display text-2xl sm:text-3xl text-[color:var(--ink)]">
                Drop your bulk file here
              </p>
              <p className="mt-3 ss-mono text-[11px] uppercase tracking-[0.14em] text-[color:var(--grey)]">
                .xlsx only · max 30MB · or click to browse
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => { if (e.target.files[0]) acceptFile(e.target.files[0]); e.target.value = ""; }}
                data-testid="file-input"
              />
            </div>
          ) : (
            <div className="border-2 border-[color:var(--ink)] p-5 flex items-center justify-between gap-4 bg-white" data-testid="uploaded-file">
              <div className="flex items-center gap-3 min-w-0">
                <FileSpreadsheet size={22} strokeWidth={1.75} className="text-[color:var(--green)] shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[color:var(--ink)] truncate" data-testid="uploaded-file-name">{file.name}</p>
                  <p className="ss-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--grey)] mt-1">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <button
                onClick={() => setFile(null)}
                className="text-[color:var(--bad)] shrink-0"
                data-testid="remove-file-btn"
                aria-label="Remove file"
              >
                <X size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Brand + Marketplace fields */}
        <div className="mt-8 ss-panel px-6 sm:px-8 py-8 space-y-6 anim-fade-up anim-d5">
          <div>
            <label className="ss-label">Your brand name *</label>
            <input
              type="text"
              value={brand}
              onChange={(e) => { setBrand(e.target.value); if (brandError) setBrandError(null); }}
              className={`ss-input ${brandError ? "ss-input--error" : ""}`}
              placeholder="e.g. Acme"
              data-testid="brand-input"
            />
            {brandError ? (
              <p className="ss-mono text-xs text-[color:var(--bad)] mt-2" data-testid="brand-error">{brandError}</p>
            ) : (
              <p className="text-xs text-[color:var(--grey)] mt-2">
                So we can separate your branded searches from everything else.
              </p>
            )}
          </div>

          <div>
            <label className="ss-label">Marketplace / currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="ss-input ss-select"
              data-testid="currency-select"
            >
              {CURRENCIES.map((c) => (
                <option key={c.value || "auto"} value={c.value}>{c.label}</option>
              ))}
            </select>
            <p className="text-xs text-[color:var(--grey)] mt-2">
              Only needed if your report doesn't include it.
            </p>
          </div>
        </div>

        {/* Submit */}
        <div className="mt-10">
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="ss-btn-primary"
            data-testid="build-scorecard-btn"
          >
            Build my scorecard →
          </button>
          {!file && (
            <p className="mt-3 ss-mono text-[11px] uppercase tracking-[0.12em] text-[color:var(--grey)]">
              Drop your bulk file above to get started.
            </p>
          )}
          {file && !brand.trim() && (
            <p className="mt-3 ss-mono text-[11px] uppercase tracking-[0.12em] text-[color:var(--grey)]">
              Add your brand name to continue.
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
